import { inject, Injectable } from '@angular/core';
import { Functions } from '@angular/fire/functions';
import { DocumentReference, Firestore } from '@angular/fire/firestore';
import { map, of, shareReplay, switchMap, tap } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { getUserRef } from '../../../libs/firestore/refs/user.refs';
import { getDoc } from '../../../libs/firestore/utils/firestore.utils';
import { CreateAdminUserResponse } from '../callables/create-admin-user.types';
import { Organization } from '../../../libs/models/organization/organization.model';
import { OrganizationUser } from '../../../libs/models/organization/organization-user.model';
import { getOrganizationUserRefFromPath } from '../../../libs/firestore/refs/organization.refs';
import { onCallCreateOrganizationAdminUser } from '../callables/create-organization-admin-user.callable';
import { onCallCreateAdminUser } from '../callables/create-admin-user.callable';
import { distinctUntilRefChanged } from '../../../shared/utils/rxjs.utils';
import { logInfo } from '../../logger/utils/logger.utils';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  // DI
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly authSvc = inject(AuthService);

  // Streams
  readonly currentUser$ = this.authSvc.currentAuthState$.pipe(
    switchMap((authUser) => {
      if (!authUser) {
        return of(null);
      }
      const userRef = getUserRef(this.firestore, authUser.uid);
      return getDoc(userRef);
    }),
    map((user) => user ?? null),
    distinctUntilRefChanged(),
    tap((user) => logInfo('Current user changed:', user)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Callables
  async createAdminUser(firstName: string, lastName: string, email: string): Promise<CreateAdminUserResponse> {
    const fn = onCallCreateAdminUser(this.functions);
    const result = await fn({ firstName, lastName, email });
    logInfo('onCallCreateAdminUser → result', result);
    return result;
  }
  async createOrganizationAdminUser(
    firstName: string,
    lastName: string,
    email: string,
    organizationRef: DocumentReference<Organization>
  ): Promise<{ organizationUserRef: DocumentReference<OrganizationUser> }> {
    const organizationPath = organizationRef.path;
    const fn = onCallCreateOrganizationAdminUser(this.functions);
    const result = await fn({ firstName, lastName, email, organizationPath });
    logInfo('callCreateOrganizationAdminUser → result', result);
    const organizationUserRef = getOrganizationUserRefFromPath(this.firestore, result.organizationUserPath);
    return { organizationUserRef };
  }
}
